import { useNavigate } from 'react-router-dom';
import { navigationDebugger } from '@/utils/navigationDebugger';

/**
 * Enhanced navigation hook with debugging and better error handling
 */
export function useNavigationHelper() {
  const navigate = useNavigate();

  const navigateWithDebug = (to: string, source?: string) => {
    try {
      // Log the navigation attempt
      if (import.meta.env.DEV) {
        console.log(`🧭 [Navigate] ${source || 'unknown'} -> ${to}`);
      }
      
      navigate(to);
    } catch (error) {
      console.error('Navigation failed:', error);
      // Fallback to window.location only if absolutely necessary
      console.warn('⚠️ Falling back to window.location navigation');
      navigationDebugger.logWindowNavigation(to, `${source} (fallback)`);
      window.location.href = to;
    }
  };

  const navigateReplace = (to: string, source?: string) => {
    try {
      if (import.meta.env.DEV) {
        console.log(`🧭 [Navigate Replace] ${source || 'unknown'} -> ${to}`);
      }
      
      navigate(to, { replace: true });
    } catch (error) {
      console.error('Replace navigation failed:', error);
      console.warn('⚠️ Falling back to window.location navigation');
      navigationDebugger.logWindowNavigation(to, `${source} (replace fallback)`);
      window.location.href = to;
    }
  };

  const safeReload = async (source?: string) => {
    const reloadSource = source || 'unknown';
    
    // Loop guard for tab switch causes - prevent immediate hard reloads
    if (reloadSource.includes('tab') || reloadSource.includes('visibility')) {
      const lastTabReload = sessionStorage.getItem('lastTabReload');
      const now = Date.now();
      if (lastTabReload && (now - parseInt(lastTabReload)) < 5000) {
        console.log(`🛑 [SafeReload] Loop guard: Ignoring rapid tab-related reload (${reloadSource})`);
        return;
      }
      sessionStorage.setItem('lastTabReload', now.toString());
    }
    
    console.log(`🔄 [SafeReload] Step 1: Starting soft-first reload for "${reloadSource}"`);
    
    // Mark reload attempt for debugging
    if (import.meta.env.DEV) {
      console.log(`🔄 [SafeReload] Marking reload attempt: ${reloadSource}`);
    }
    
    try {
      // Step 1: React Router replace navigation
      const currentPath = window.location.pathname + window.location.search;
      console.log(`🔄 [SafeReload] Step 2: Attempting React Router replace navigation to: ${currentPath}`);
      navigate(currentPath, { replace: true });
      
      // Give React time to process
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log(`🔄 [SafeReload] Step 3: React Router navigation completed successfully`);
      return; // Success - no further action needed
      
    } catch (error) {
      console.warn(`⚠️ [SafeReload] Step 2 failed (React Router replace) for ${reloadSource}:`, error);
    }
    
    try {
      // Step 2: History API manipulation
      console.log(`🔄 [SafeReload] Step 4: Attempting history.pushState + popstate`);
      window.history.pushState(null, '', window.location.href);
      window.dispatchEvent(new PopStateEvent('popstate'));
      await new Promise(resolve => setTimeout(resolve, 300));
      
      console.log(`🔄 [SafeReload] Step 5: History manipulation completed`);
      return; // Success
      
    } catch (error) {
      console.warn(`⚠️ [SafeReload] Step 4 failed (history manipulation):`, error);
    }
    
    // Step 3: Last resort - Hard reload with tab-aware delay
    const lastVis = (window as any).__lastVisibilityChange || 0;
    const sinceVis = Date.now() - lastVis;
    let delay = document.hidden ? 6000 : 3500;
    if (sinceVis < 3000) delay = Math.max(delay, 5000);
    console.error(`🔄 [SafeReload] All soft methods failed for "${reloadSource}", hard reload in ${delay}ms`);
    
    if (import.meta.env.DEV) {
      console.log(`🔄 [SafeReload] Hard fallback marked: ${reloadSource}`, {
        delay,
        sinceVis
      });
    }
    
    setTimeout(() => {
      // Avoid hard reloads to prevent tab-switch loops; attempt one more gentle refresh
      try {
        window.history.replaceState(null, '', window.location.pathname);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (e) {
        console.warn('Final navigation recovery attempt failed; please refresh manually if needed.', e);
      }
    }, delay);
  };

  return {
    navigate: navigateWithDebug,
    navigateReplace,
    safeReload,
    // Expose original navigate for cases where debugging isn't needed
    originalNavigate: navigate
  };
}